/**
 * Complete Production-Grade Security Audit & SIEM Telemetry Route — Module 12.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { siteConfig } from "@/config/site";
import { ShieldCheck, Lock, Activity, AlertTriangle, Key } from "lucide-react";
import { DataTable, type ColumnDef } from "@/components/admin/common/data-table";
import {
  fetchSecurityLogsFromSupabase,
  type SecurityAuditLog,
} from "@/features/admin/users/store/admin-users-store";

export const Route = createFileRoute("/admin/security")({
  head: () => ({
    meta: [{ title: `Security Audit Logs — Admin — ${siteConfig.name}` }],
  }),
  component: AdminSecurityPage,
});

function AdminSecurityPage() {
  const [logs, setLogs] = useState<SecurityAuditLog[]>([]);

  useEffect(() => {
    fetchSecurityLogsFromSupabase().then((data) => {
      if (data) setLogs(data);
    });
  }, []);

  const columns: ColumnDef<SecurityAuditLog>[] = [
    {
      id: "action",
      header: "Security Event Action",
      accessorKey: "action",
      cell: (row) => (
        <div>
          <span className="font-mono font-bold text-foreground text-xs block">{row.action}</span>
          <span className="text-[11px] text-muted-foreground">{row.details}</span>
        </div>
      ),
      sortable: true,
    },
    {
      id: "user",
      header: "Identity User",
      accessorKey: "user",
      cell: (row) => <span className="font-mono text-xs font-semibold">{row.user}</span>,
      sortable: true,
    },
    {
      id: "ipAddress",
      header: "IP Address",
      accessorKey: "ipAddress",
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground">{row.ipAddress}</span>
      ),
    },
    {
      id: "severity",
      header: "Severity",
      accessorKey: "severity",
      cell: (row) => (
        <span
          className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
            row.severity === "CRITICAL"
              ? "bg-rose-100 text-rose-800"
              : row.severity === "WARNING"
                ? "bg-amber-100 text-amber-800"
                : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {row.severity}
        </span>
      ),
      sortable: true,
    },
    {
      id: "timestamp",
      header: "Timestamp",
      accessorKey: "timestamp",
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {new Date(row.timestamp).toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="space-y-6 pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">SIEM & Identity Telemetry</p>
          <h1 className="mt-1 font-display text-3xl text-foreground">Security Audit Logs</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Real-time security telemetry, administrative audit trails, IP rate-limit blocks, and
            authorization events.
          </p>
        </div>
      </div>

      <DataTable
        data={logs}
        columns={columns}
        keyExtractor={(row) => row.id}
        searchPlaceholder="Search audit log by action, user, or IP address..."
        searchField={(row) => `${row.action} ${row.user} ${row.ipAddress}`}
      />
    </div>
  );
}
